import { CONNECTOR_LIMITS, CONNECTOR_METHODS, type ConnectorMethod } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Field, Input, Segmented, Textarea } from "@sagot/ui";
import { IntegerInput } from "../../../workspace/shared";
import type { HttpRequestStep } from "../types";
import { bodyAllowed, bodyProblem, CONNECTOR_EXAMPLES, pathProblem, secondsOf, timeoutCapOf } from "./connector-model";
import { RowsEditor, useRows } from "./connector-rows";
import type { FormProps } from "./webhook-form";

const C = "engage.workflows.connector";

/** Method, path, query parameters and headers of the request. */
export function RequestLine({ step, disabled, onChange }: FormProps<HttpRequestStep>) {
  const { t } = useTranslation();
  const query = useRows(step.query, (record) => { onChange({ ...step, query: record }); });
  const headers = useRows(step.headers, (record) => { onChange({ ...step, headers: record }); });
  const path = pathProblem(step.path);
  return (
    <div className="space-y-4">
      <div>
        <span className="label">{t(`${C}.fields.method`)}</span>
        <Segmented
          aria-label={t(`${C}.fields.method`)}
          value={step.method}
          onValueChange={(method: ConnectorMethod) => { onChange({ ...step, method }); }}
          options={CONNECTOR_METHODS.map((value) => ({ value, label: value }))}
        />
      </div>
      <Field label={t(`${C}.fields.path`)} hint={t(`${C}.fields.pathHint`, { example: CONNECTOR_EXAMPLES.path })} error={path ? t(`${C}.problems.path.${path}`, { max: CONNECTOR_LIMITS.path }) : undefined}>
        <Input className="font-mono text-xs" placeholder={CONNECTOR_EXAMPLES.path} disabled={disabled} maxLength={CONNECTOR_LIMITS.path} value={step.path ?? ""} onChange={(event) => { onChange({ ...step, path: event.target.value || undefined }); }} />
      </Field>
      <RowsEditor kind="query" label={t(`${C}.rows.query.label`)} addLabel={t(`${C}.rows.query.add`)} namePlaceholder={t(`${C}.rows.query.namePlaceholder`)} valuePlaceholder={t(`${C}.rows.query.valuePlaceholder`)} rows={query.rows} disabled={disabled} onChange={query.change} />
      <RowsEditor kind="header" label={t(`${C}.rows.headers.label`)} hint={t(`${C}.rows.headers.hint`)} addLabel={t(`${C}.rows.headers.add`)} namePlaceholder={t(`${C}.rows.headers.namePlaceholder`)} valuePlaceholder={t(`${C}.rows.headers.valuePlaceholder`)} rows={headers.rows} disabled={disabled} onChange={headers.change} />
    </div>
  );
}

/** The JSON body of a POST, PUT or PATCH; a GET has none (one left over from another method is flagged and can be cleared). */
export function RequestBody({ step, disabled, onChange }: FormProps<HttpRequestStep>) {
  const { t } = useTranslation();
  if (!bodyAllowed(step.method)) {
    return (
      <div className="space-y-2" data-testid="connector-body-none">
        <p className="text-xs text-fg-muted">{t(`${C}.fields.bodyNone`)}</p>
        {step.body ? (
          <>
            <p role="alert" className="text-xs font-semibold text-danger">{t(`${C}.fields.bodyOnGet`)}</p>
            <Button type="button" variant="secondary" size="sm" disabled={disabled} onClick={() => { onChange({ ...step, body: undefined }); }}>{t(`${C}.fields.clearBody`)}</Button>
          </>
        ) : null}
      </div>
    );
  }
  const problem = bodyProblem(step.body);
  return (
    <Field label={t(`${C}.fields.body`)} hint={t(`${C}.fields.bodyHint`, { example: CONNECTOR_EXAMPLES.quotedVariable })} error={problem ? t(`${C}.problems.body.${problem}`) : undefined}>
      <Textarea rows={6} spellCheck={false} className="font-mono text-xs" maxLength={CONNECTOR_LIMITS.body} placeholder={CONNECTOR_EXAMPLES.body} disabled={disabled} value={step.body ?? ""} onChange={(event) => { onChange({ ...step, body: event.target.value || undefined }); }} />
    </Field>
  );
}

/** How long to wait for the answer; a workflow the visitor sees waits less. */
export function TimeoutField({ step, disabled, facing, onChange }: FormProps<HttpRequestStep> & { facing: boolean }) {
  const { t } = useTranslation();
  const cap = timeoutCapOf(facing);
  const over = step.timeout_ms > cap;
  const hint = facing ? t(`${C}.fields.timeoutHintFacing`, { max: cap / 1000 }) : t(`${C}.fields.timeoutHint`, { max: cap / 1000 });
  return (
    <Field label={t(`${C}.fields.timeout`)} hint={hint} error={over ? hint : undefined}>
      <IntegerInput min={CONNECTOR_LIMITS.timeoutMsMin / 1000} max={cap / 1000} disabled={disabled} value={secondsOf(step.timeout_ms)} onChange={(seconds) => { onChange({ ...step, timeout_ms: seconds * 1000 }); }} />
    </Field>
  );
}
